import type { FormattedTemplate } from '../types/templates';
import {
  getTemplateSections,
  isValidQuestionConfig,
  isValidSelectQuestionConfig,
  isValidScalarQuestionConfig,
  normalizeQuestionOptions,
} from './templateUtils';

interface BaseTemplateQuestionnaireSection {
  key: string;
  label?: string;
  description?: string;
  required: boolean;
  prompt: string;
  responseType: 'select' | 'text' | 'date' | 'number' | 'boolean';
}

export interface TemplateQuestionnaireSelectSection
  extends BaseTemplateQuestionnaireSection {
  responseType: 'select';
  selectionMode: 'radio' | 'checkbox';
  options: string[];
  maxSelections?: number;
  minSelections?: number;
}

export interface TemplateQuestionnaireScalarSection
  extends BaseTemplateQuestionnaireSection {
  responseType: 'text' | 'date' | 'number' | 'boolean';
}

export type TemplateQuestionnaireSection =
  | TemplateQuestionnaireSelectSection
  | TemplateQuestionnaireScalarSection;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string')
  );
}

export function getQuestionLabel(
  section: Pick<TemplateQuestionnaireSection, 'key' | 'label'>
): string {
  return isNonEmptyString(section.label) ? section.label : section.key;
}

export function getTemplateQuestionnaireQuestions(
  template: FormattedTemplate | null | undefined
): TemplateQuestionnaireSection[] {
  return getTemplateSections(template).flatMap((section) => {
    if (!isObject(section)) {
      return [];
    }

    if (section.type === 'questionnaire') {
      const questions = section.questions;
      if (!Array.isArray(questions)) {
        return [];
      }

      return questions.flatMap((question): TemplateQuestionnaireSection[] => {
        if (isValidScalarQuestionConfig(question)) {
          return [
            {
              key: question.questionId,
              label: isNonEmptyString(question.label)
                ? question.label
                : question.question,
              description:
                typeof question.description === 'string'
                  ? question.description
                  : undefined,
              required: question.required,
              prompt: question.question,
              responseType: question.responseType,
            },
          ];
        }

        if (!isValidSelectQuestionConfig(question)) {
          return [];
        }
        const options = normalizeQuestionOptions(question.answerOptions);

        return [
          {
            key: question.questionId,
            label: isNonEmptyString(question.label)
              ? question.label
              : question.question,
            description:
              typeof question.description === 'string'
                ? question.description
                : undefined,
            required: question.required,
            prompt: question.question,
            responseType: 'select' as const,
            selectionMode: question.selectionMode,
            options,
            minSelections: question.selectionMode === 'radio' ? 1 : 0,
            maxSelections: question.selectionMode === 'radio' ? 1 : undefined,
          },
        ];
      });
    }

    if (section.type !== 'multi_select') {
      return [];
    }

    const config = section.multiSelectConfig;
    if (
      !isObject(config) ||
      !isStringArray(config.options) ||
      config.options.length === 0
    ) {
      return [];
    }

    if (!isNonEmptyString(section.key) || !isNonEmptyString(section.prompt)) {
      return [];
    }

    return [
      {
        key: section.key,
        label: isNonEmptyString(section.label) ? section.label : undefined,
        required: Boolean(section.required),
        prompt: section.prompt,
        responseType: 'select',
        selectionMode:
          typeof config.maxSelections === 'number' && config.maxSelections === 1
            ? 'radio'
            : 'checkbox',
        options: config.options,
        minSelections:
          typeof config.minSelections === 'number'
            ? config.minSelections
            : undefined,
        maxSelections:
          typeof config.maxSelections === 'number'
            ? config.maxSelections
            : undefined,
      },
    ];
  });
}

export function getTemplateQuestionnaireQuestionKeys(
  template: FormattedTemplate | null | undefined
): string[] {
  const questionKeys: string[] = [];

  getTemplateSections(template).forEach((section) => {
    if (
      !isObject(section) ||
      section.type !== 'questionnaire' ||
      !Array.isArray(section.questions)
    ) {
      return;
    }

    section.questions.forEach((question) => {
      if (!isValidQuestionConfig(question)) {
        return;
      }

      questionKeys.push(question.questionId);
    });
  });

  return questionKeys;
}

export function isSingleAnswerQuestion(
  section: Pick<TemplateQuestionnaireSelectSection, 'selectionMode'>
): boolean {
  return section.selectionMode === 'radio';
}
